import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { isMemberBlocked } from 'utils/member-utils';

const UnallocatedMembersTable = ({ 
  members, 
  onRemoveMember, 
  onEditService, 
  selectedMembers, 
  onMemberSelect, 
  onSelectAll,
  loading 
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedAndFilteredMembers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    let filteredMembers = !query
      ? [...members]
      : members.filter((member) =>
          (member.name && member.name.toLowerCase().includes(query)) ||
          (member.mobNo && member.mobNo.toString().includes(searchTerm.trim())) ||
          (member.district && member.district.toLowerCase().includes(query)) ||
          (member.samithi && member.samithi.toLowerCase().includes(query)) ||
          (member.saiConnectId && member.saiConnectId.toLowerCase().includes(query))
        );

    if (sortConfig.key) {
      filteredMembers = [...filteredMembers].sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (sortConfig.key === 'age') {
          const aNum = Number(aVal) || 0;
          const bNum = Number(bVal) || 0;
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }

        const aStr = (aVal ?? '').toString().trim();
        const bStr = (bVal ?? '').toString().trim();
        const cmp = aStr.localeCompare(bStr, undefined, {
          sensitivity: 'base',
          numeric: true,
        });
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }

    return filteredMembers;
  }, [members, sortConfig, searchTerm]);

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <Icon name="ArrowUpDown" size={16} className="text-muted-foreground" />;
    }
    return sortConfig.direction === 'asc' 
      ? <Icon name="ArrowUp" size={16} className="text-primary" />
      : <Icon name="ArrowDown" size={16} className="text-primary" />;
  };

  const allSelected = selectedMembers.length === sortedAndFilteredMembers.length && sortedAndFilteredMembers.length > 0;
  const someSelected = selectedMembers.length > 0 && selectedMembers.length < sortedAndFilteredMembers.length;

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="p-6">
          <div className="animate-spiritual-pulse space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center space-x-4">
                <div className="w-4 h-4 bg-muted rounded"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-1/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
                <div className="flex space-x-2">
                  <div className="w-16 h-8 bg-muted rounded"></div>
                  <div className="w-16 h-8 bg-muted rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden shadow-spiritual-sm">
      {/* Table Header with Search */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="flex items-center space-x-3">
            <h3 className="font-heading font-semibold text-foreground">
              Unallocated Members
            </h3>
            <span className="bg-error/10 text-error px-2 py-1 rounded-full text-xs font-caption">
              {sortedAndFilteredMembers.length} members
            </span>
          </div>
          
          <div className="flex items-center space-x-3">
            <Input
              type="search"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64"
            />
            
            {selectedMembers.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  selectedMembers.forEach(memberId => {
                    const member = members.find(m => m.id === memberId);
                    if (member) onRemoveMember(member);
                  });
                }}
                iconName="Trash2"
                iconPosition="left"
              >
                Remove Selected ({selectedMembers.length})
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              {/* <th className="w-12 px-4 py-3 text-left">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(e) => onSelectAll(e.target.checked)}
                />
              </th> */}
              
              {[
                { key: 'name', label: 'Name' },
                { key: 'mobNo', label: 'Mobile' },
                { key: 'district', label: 'District' },
                { key: 'samithi', label: 'Samithi' },
                { key: 'age', label: 'Age' }
              ].map((column) => (
                <th key={column.key} className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort(column.key)}
                    className="flex items-center space-x-1 font-heading font-medium text-foreground hover:text-primary transition-colors"
                  >
                    <span>{column.label}</span>
                    {getSortIcon(column.key)}
                  </button>
                </th>
              ))}
              
              <th className="px-4 py-3 text-left font-heading font-medium text-foreground">
                Actions
              </th>
            </tr>
          </thead>
          
          <tbody className="divide-y divide-border">
            {sortedAndFilteredMembers.map((member, index) => {
              const blocked = isMemberBlocked(member);
              return (
              <tr 
                key={member.id} 
                className={`transition-colors ${
                  blocked 
                    ? 'bg-red-50/80 dark:bg-red-950/20 hover:bg-red-100/80 border-l-4 border-l-red-500' 
                    : index % 2 === 0 ? 'hover:bg-muted/30' : 'zebra-stripe hover:bg-muted/30'
                }`}
              >
                {/* <td className="px-4 py-3">
                  <Checkbox
                    checked={selectedMembers.includes(member.id)}
                    onChange={(e) => onMemberSelect(member.id, e.target.checked)}
                  />
                </td> */}
                
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      blocked ? 'bg-red-100 dark:bg-red-900/50' : 'bg-primary/10'
                    }`}>
                      <Icon name="User" size={16} className={blocked ? 'text-red-600' : 'text-primary'} />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className={`font-medium ${blocked ? 'text-red-900 dark:text-red-300 font-semibold' : 'text-foreground'}`}>
                          {member?.name ? member?.name : "---"}
                        </p>
                        {blocked && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-300 dark:bg-red-900/50 dark:text-red-300 dark:border-red-700">
                            Blocked
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground font-mono">
                        ID: {member.saiConnectId ? member.saiConnectId :'---'}
                      </p>
                    </div>
                  </div>
                </td>
                
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-2">
                  { member?.mobNo  && <Icon name="Phone" size={14} className="text-muted-foreground" />}
                    <span className="font-mono text-sm">{member?.mobNo ? member?.mobNo :"---" }</span>
                  </div>
                </td>
                
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <Icon name="MapPin" size={14} className="text-muted-foreground" />
                    <span className="text-sm">{member.district ? member.district:'---'}</span>
                  </div>
                </td>
                
                <td className="px-4 py-3">
                  <span className="text-sm">{member.samithi?member.samithi:'---'}</span>
                </td>
                
                <td className="px-4 py-3">
                  <span className="text-sm font-mono">{member.age  ?`${member.age} years`:'---'} </span>
                </td>
                
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEditService(member)}
                      iconName="Edit"
                      iconPosition="left"
                    >
                      Edit Service
                    </Button>
                    
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => onRemoveMember(member)}
                      iconName="Trash2"
                      iconPosition="left"
                    >
                      Remove
                    </Button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden divide-y divide-border">
        {sortedAndFilteredMembers.map((member) => {
          const blocked = isMemberBlocked(member);
          return (
          <div 
            key={member.id} 
            className={`p-4 transition-colors ${
              blocked 
                ? 'bg-red-50/80 dark:bg-red-950/20 border-l-4 border-l-red-500' 
                : ''
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  checked={selectedMembers.includes(member.id)}
                  onChange={(e) => onMemberSelect(member.id, e.target.checked)}
                />
                
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  blocked ? 'bg-red-100 dark:bg-red-900/50' : 'bg-primary/10'
                }`}>
                  <Icon name="User" size={18} className={blocked ? 'text-red-600' : 'text-primary'} />
                </div>
                
                <div>
                  <div className="flex items-center space-x-2">
                    <h4 className={`font-heading font-medium ${blocked ? 'text-red-900 dark:text-red-300 font-semibold' : 'text-foreground'}`}>
                      {member.name}
                    </h4>
                    {blocked && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-300 dark:bg-red-900/50 dark:text-red-300 dark:border-red-700">
                        Blocked
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">
                    ID: {member.saiConnectId}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2 mb-4">
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Phone" size={14} className="text-muted-foreground" />
                <span className="font-mono">{member.mobNo}</span>
              </div>
              
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="MapPin" size={14} className="text-muted-foreground" />
                <span>{member.district} • {member.samithi}</span>
              </div>
              
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Calendar" size={14} className="text-muted-foreground" />
                <span>{member.age} years old</span>
              </div>
            </div>
            
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEditService(member)}
                iconName="Edit"
                iconPosition="left"
                fullWidth
              >
                Edit Service
              </Button>
              
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onRemoveMember(member)}
                iconName="Trash2"
                iconPosition="left"
                fullWidth
              >
                Remove
              </Button>
            </div>
          </div>
          );
        })}
      </div>

      {/* Empty State */}
      {sortedAndFilteredMembers.length === 0 && !loading && (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="font-heading font-medium text-foreground mb-2">
            No Unallocated Members
          </h3>
          <p className="text-muted-foreground">
            {searchTerm ? 'No members match your search criteria' : 'All members have been allocated to services'}
          </p>
        </div>
      )}
    </div>
  );
};

export default UnallocatedMembersTable;